import { filterConfig } from "@modules/global/filter-config.ts"
import type { Filters } from "../types"
import { getCategories } from "./category"
import { getDisplayGroup } from "./filter-group-name-map"
import { getSearchText } from "./filter-selection/search-text-data"

export const getFilterContainerElement = () => document.querySelector<HTMLDivElement>(".head-menu")
export const loaderHtmlString = () => '<span class="loading">&nbsp;</span>'

let filters: Filters = {}

export function setFilters(newFilters: Filters) {
    filters = newFilters
}

export function getFilters() {
    return filters
}

export function getGroupedFilters() {
    const grouped: { [key: string]: { values: string[]; groupName: string } } = {}
    for (const group of Object.keys(filters)) {
        for (const value of filters[group]) {
            const displayGroup = getDisplayGroup(group, value)
            if (grouped[displayGroup]) {
                grouped[displayGroup].values.push(value)
            } else {
                grouped[displayGroup] = {
                    groupName: group,
                    values: [value],
                }
            }
        }
    }
    return grouped
}

export function getGroupedFilterKeysService() {
    const grouped = getGroupedFilters()
    return Object.keys(grouped)
        .filter((key) => grouped[key].groupName === "service" && key !== "exklusiv")
        .sort()
}

export function getGroupedFilterKeysContact() {
    const grouped = getGroupedFilters()
    return Object.keys(grouped)
        .filter((key) => filterConfig.groups.contact.includes(grouped[key].groupName))
        .sort()
}

export function getGroupedFilterKeysTraits() {
    const grouped = getGroupedFilters()
    return Object.keys(grouped)
        .filter(
            (key) =>
                !filterConfig.groups.contact.includes(grouped[key].groupName) &&
                grouped[key].groupName !== "service" &&
                grouped[key].groupName !== "exklusiv",
        )
        .sort()
}

export function removeGroupedFilterGroup(displayGroup: string) {
    const grouped = getGroupedFilters()
    const valuesToRemove = grouped[displayGroup].values
    const groupName = grouped[displayGroup].groupName
    filters[groupName] = filters[groupName].filter((val: string) => !valuesToRemove.includes(val))
    if (filters[groupName].length === 0) {
        delete filters[groupName]
    }
}

export function removeFilterGroupValue(group: string, value: string) {
    if (!filters[group]) {
        return
    }
    if (filters[group].length > 1) {
        filters[group] = filters[group].filter((groupValue: string) => groupValue !== value)
    } else {
        delete filters[group]
    }
}

export function addFilterGroupValue(group: string, value: string) {
    if (group in filters) {
        if (!filters[group].includes(value)) filters[group].push(value)
    } else {
        filters[group] = [value]
    }
}

export function setFilterGroupValue(group: string, value: string[]) {
    filters[group] = value
}

export function removeFilterGroup(group: string) {
    delete filters[group]
}

export function getFiltersAsString() {
    return Object.keys(filters)
        .map(
            (group) =>
                `${group}${filterConfig.delimiterGroupValue}${filters[group].join(filterConfig.delimiterValues)}`,
        )
        .join(filterConfig.delimiterGroups)
}

export function isActiveStarsFilter(): boolean {
    return <boolean>(
        (getFilters().exklusiv?.includes("mit-stars-content") ||
            getFilters().exklusiv?.includes("mit-premium-beitraegen"))
    )
}

export function isPremiumStarsFilter() {
    return getFilters().exklusiv?.includes("mit-premium-beitraegen")
}

export function isAnyFilterActive() {
    return Object.keys(getFilters()).length > 0 || getCategories().length > 0 || !!getSearchText()
}

export function notOnlyCustomCategoriesActive() {
    let lengthToCheck = 1
    if (getCategories().includes("onlineservice")) {
        lengthToCheck++
    }
    if (getCategories().includes("telefonsex")) {
        lengthToCheck++
    }

    return lengthToCheck !== 1 && getCategories().length >= lengthToCheck
}

export function telefonsexCategoryActive() {
    return getCategories().includes("telefonsex")
}

export function onlinserviceCategoryActive() {
    return getCategories().includes("onlineservice")
}

export function parseFiltersFromLocation() {
    filters = {}

    const pathSegments = location.pathname.split("/")
    const filterString = pathSegments[pathSegments.length - 1]

    // filter string always includes a group delimiter, categories wont include that
    if (filterString?.includes(filterConfig.delimiterGroupValue)) {
        const groupsString = filterString.split(filterConfig.delimiterGroups)
        for (const groupString of groupsString) {
            if (!groupString) {
                continue
            }
            const [group, valuesString] = groupString.split(filterConfig.delimiterGroupValue)
            filters[group] = valuesString.split(filterConfig.delimiterValues)
        }
    }
}
type AgeSelects = {
    minAgeSelect: HTMLSelectElement | null
    maxAgeSelect: HTMLSelectElement | null
}

function getAgeSelects(): AgeSelects {
    return {
        minAgeSelect: document.querySelector<HTMLSelectElement>('select[name="min-age"]'),
        maxAgeSelect: document.querySelector<HTMLSelectElement>('select[name="max-age"]'),
    }
}
function updateMaxAgeOptions() {
    const { minAgeSelect, maxAgeSelect } = getAgeSelects()

    if (!minAgeSelect || !maxAgeSelect) {
        return
    }

    const minAge = Number.parseInt(minAgeSelect.value, 10)

    for (let i = 0; i < maxAgeSelect.options.length; i++) {
        const option = maxAgeSelect.options[i]

        if (option.value === "") {
            option.hidden = false
            continue
        }

        const age = Number.parseInt(option.value, 10)
        option.hidden = !Number.isNaN(minAge) && age < minAge
    }
}

function updateMinAgeOptions() {
    const { minAgeSelect, maxAgeSelect } = getAgeSelects()

    if (!minAgeSelect || !maxAgeSelect) {
        return
    }

    const maxAge = Number.parseInt(maxAgeSelect.value, 10)

    for (let i = 0; i < minAgeSelect.options.length; i++) {
        const option = minAgeSelect.options[i]

        if (option.value === "") {
            option.hidden = false
            continue
        }

        const age = Number.parseInt(option.value, 10)
        option.hidden = !Number.isNaN(maxAge) && age > maxAge
    }
}

function initAgeFilters() {
    const { minAgeSelect, maxAgeSelect } = getAgeSelects()

    if (!minAgeSelect || !maxAgeSelect) {
        return
    }

    minAgeSelect.addEventListener("change", () => {
        updateMaxAgeOptions()
        updateMinAgeOptions()
    })

    maxAgeSelect.addEventListener("change", () => {
        updateMaxAgeOptions()
        updateMinAgeOptions()
    })

    updateMaxAgeOptions()
    updateMinAgeOptions()
}

export function resetAgeFilters() {
    const { minAgeSelect, maxAgeSelect } = getAgeSelects()

    if (!minAgeSelect || !maxAgeSelect) {
        return
    }

    minAgeSelect.value = ""
    maxAgeSelect.value = ""

    for (let i = 0; i < minAgeSelect.options.length; i++) {
        minAgeSelect.options[i].hidden = false
    }

    for (let i = 0; i < maxAgeSelect.options.length; i++) {
        maxAgeSelect.options[i].hidden = false
    }
}

document.addEventListener("DOMContentLoaded", initAgeFilters)

parseFiltersFromLocation()
